import React, { useState, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import Dashboard from '../components/Dashboard'
import Login from '../components/Login'
import { useAuth } from '../contexts/AuthContext'
import { GatewayData } from '../types'
import { fetchGatewayDataFromSupabase } from '../lib/supabaseDataProcessing'

interface AppState {
  gatewayData: GatewayData[]
  isConnected: boolean
  dataSource: string
  isLoading: boolean
  error: string | null
}

const App: React.FC = () => {


  const { user, isLoading: authLoading } = useAuth();
  
  const [state, setState] = useState<AppState>({
    gatewayData: [],
    isConnected: false,
    dataSource: '',
    isLoading: true,
    error: null
  })

  const mapSettings = {
    center: {
      lat: import.meta.env.VITE_DEFAULT_MAP_LAT ? parseFloat(import.meta.env.VITE_DEFAULT_MAP_LAT) : 22.373989,
      lng: import.meta.env.VITE_DEFAULT_MAP_LNG ? parseFloat(import.meta.env.VITE_DEFAULT_MAP_LNG) : 114.193266,
    },
    zoom: import.meta.env.VITE_DEFAULT_MAP_ZOOM ? parseInt(import.meta.env.VITE_DEFAULT_MAP_ZOOM) : 15,
  }

  const fetchData = async (isInitialLoad = false) => {
    try {
      // Only show loading screen on initial load, not on refresh
      if (isInitialLoad) {
        setState(prev => ({ ...prev, isLoading: true, error: null }))
      }
      const gatewayData = await fetchGatewayDataFromSupabase()

      setState(prev => ({
        ...prev,
        gatewayData,
        isConnected: true,
        dataSource: "Supabase",
        isLoading: false,
        error: null
      }))
    } catch (supabaseError) {
      setState(prev => ({
        ...prev,
        gatewayData: [],
        isConnected: false,
        dataSource: "Supabase - Error",
        isLoading: false,
        error: supabaseError instanceof Error ? supabaseError.message : 'Unknown error'
      }))
    }
  }

  useEffect(() => {
    // Initial load with loading screen
    fetchData(true)

    // Set up automatic refresh every 30 seconds (without loading screen)
    const interval = setInterval(() => fetchData(false), 30000)

    return () => clearInterval(interval)
  }, [])

  // Show loading screen while checking authentication
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <h2 className="text-xl">Loading water monitoring data...</h2>
        </div>
      </div>
    )
  }

  // If user is not authenticated, show login page
  if (!user) {
    return <Login />;
  }

  // If user is authenticated but data is still loading
  if (state.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white mx-auto mb-4"></div>
          <h2 className="text-xl">Loading...</h2>
        </div>
      </div>
    )
  }

  // If database connection failed
  if (!state.isConnected) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">
        <div className="text-center">
          <h2 className="text-xl mb-4">
            Failed to connect to database. Check your Supabase configuration.
          </h2>
          {state.error && (
            <p className="text-red-400 mb-4">Error: {state.error}</p>
          )}
          <button
            onClick={() => fetchData(true)}
            className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded text-white"
          >
            Retry Connection
          </button>
        </div>
      </div>
    )
  }

  // User is authenticated and data is loaded - show dashboard
  return (
    <Routes>
      <Route
        path="/"
        element={<Dashboard initialGatewayData={state.gatewayData} mapSettings={mapSettings} />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
